import { Flex, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
  buildFeishuQrAuthorizeUrl,
  createFeishuQrLogin,
  handleFeishuQrMessage,
  loadFeishuQrSdk,
  resolveFeishuQrSdk,
} from '@/apps/auth/utils/feishu-qr';
import { resolveLoginErrorMessage } from '@/apps/auth/utils/login';
import { authClient } from '@/common/utils/auth-client';

const { Text } = Typography;
const QR_CONTAINER_ID = 'feishu_qr_login';

const FeishuQrLoginForm = ({ callbackURL, errorCallbackURL, onError }) => {
  const qrLoginRef = useRef(null);
  const [statusText, setStatusText] = useState('正在加载飞书扫码登录');

  useEffect(() => {
    let disposed = false;
    let messageHandler;

    const setupQrLogin = async () => {
      try {
        const { data, error, url } = await authClient.signIn.oauth2({
          providerId: 'feishu',
          callbackURL,
          errorCallbackURL,
          disableRedirect: true,
        });
        if (error) {
          throw new Error(resolveLoginErrorMessage(error, '飞书扫码登录失败，请重试'));
        }
        const oauthUrl = data?.url || url;
        if (!oauthUrl) {
          throw new Error('飞书扫码登录地址获取失败');
        }
        const goto = buildFeishuQrAuthorizeUrl(oauthUrl);

        await loadFeishuQrSdk();
        if (disposed) {
          return;
        }
        qrLoginRef.current = createFeishuQrLogin({
          qrSdk: resolveFeishuQrSdk(window),
          containerId: QR_CONTAINER_ID,
          goto,
        });
        messageHandler = (event) => {
          handleFeishuQrMessage({
            assign: window.location.assign.bind(window.location),
            event,
            goto,
            qrLogin: qrLoginRef.current,
          });
        };
        window.addEventListener('message', messageHandler, false);
        setStatusText('请使用飞书扫码登录');
      } catch (error) {
        const errorMessage = error?.message || '飞书扫码登录失败，请重试';
        setStatusText(errorMessage);
        onError(errorMessage);
      }
    };

    setupQrLogin();

    return () => {
      disposed = true;
      if (messageHandler) {
        window.removeEventListener('message', messageHandler, false);
      }
    };
  }, [callbackURL, errorCallbackURL, onError]);

  return (
    <Flex align="center" gap="middle" vertical>
      <Flex id={QR_CONTAINER_ID} align="center" justify="center" />
      <Text type="secondary">{statusText}</Text>
    </Flex>
  );
};

FeishuQrLoginForm.propTypes = {
  callbackURL: PropTypes.string.isRequired,
  errorCallbackURL: PropTypes.string.isRequired,
  onError: PropTypes.func.isRequired,
};

export default FeishuQrLoginForm;
